/*
  TOSSIT offer engine
  ===================
  The shared brain behind every upsell surface: the product-page box, the cart
  drawer, and anything added later. One file decides WHAT to offer; each
  surface only decides how to draw it.

  This is the part the app we are replacing structurally cannot do. Its
  placements are configured independently of one another, so the product page
  and the cart will happily pitch the same product twice in one session. Here
  they share a tree, a catalog, and a memory - so the cart can pick up where
  the product page left off instead of repeating it.

  The rules themselves live in snippets/tossit-upsell-rules.liquid, which is
  the only file to edit when the suggestions should change. Categories there
  are defined by COLLECTION, so adding a product to a collection in admin puts
  it in play with no code change.

  THE ENGINE NEVER DECIDES MONEY. It picks products. Every saving shown
  anywhere is a Shopify automatic discount, enforced server-side at checkout
  whatever the browser believes.
*/

window.TossitOffers = (function () {
  'use strict';

  var DISMISS_KEY = 'tossit:dismissed-offers';

  /* ------------------------------------------------------------------ *
     Dismissals, shared across surfaces for one browsing session.

     If someone closes an offer on the product page, the cart re-pitching it
     ninety seconds later is the single most irritating thing an upsell system
     does. sessionStorage rather than localStorage on purpose: a decision made
     while shopping should not follow them back next week.
   * ------------------------------------------------------------------ */

  function dismissed() {
    try {
      return JSON.parse(sessionStorage.getItem(DISMISS_KEY) || '[]');
    } catch (e) {
      return [];
    }
  }

  function dismiss(productId) {
    try {
      var list = dismissed();
      if (list.indexOf(String(productId)) === -1) {
        list.push(String(productId));
        sessionStorage.setItem(DISMISS_KEY, JSON.stringify(list));
      }
    } catch (e) {
      /* Private mode, quota, whatever - a lost dismissal is not worth an error. */
    }
  }

  /* ------------------------------------------------------------------ *
     Reading the cart
   * ------------------------------------------------------------------ */

  function indexCart(cart) {
    var haveProducts = {};
    var haveVariants = {};
    (cart && cart.items ? cart.items : []).forEach(function (i) {
      haveProducts[i.product_id] = true;
      haveVariants[i.variant_id] = true;
    });
    return { products: haveProducts, variants: haveVariants };
  }

  function categoriesInCart(tree, haveProducts) {
    var present = {};
    Object.keys((tree && tree.categories) || {}).forEach(function (key) {
      present[key] = (tree.categories[key] || []).some(function (id) {
        return haveProducts[Number(id)];
      });
    });
    return present;
  }

  /* ------------------------------------------------------------------ *
     The tree

     Rules run top to bottom, first match wins, one product per rule. A rule
     that matches but has nothing left to give - everything in its category is
     already in the cart, already offered, or dismissed - falls through to the
     next rather than ending the search.
   * ------------------------------------------------------------------ */

  /*
     The price band, shared by every source.

     This lives here rather than inside one picker because the band has to hold
     no matter WHICH source fills a slot. It did not, and the bug was visible on
     the storefront: with the expansion pack and both posters already in the
     cart, the box on a $34.99 starter pack offered a $49.99 board and a $59.99
     board - $144.97, two near-identical boards, and well past the ceiling.
     fromBundle ran out of eligible candidates, and the sources after it filled
     the slots with no idea a ceiling existed.

     `running` starts at the price of the product being viewed, because the
     ceiling is on what the customer is being asked to spend in total, not on
     the add-ons alone.
  */
  function inCats(tree, keys, pid) {
    return (keys || []).some(function (c) {
      return (((tree || {}).categories || {})[c] || []).some(function (x) {
        return String(x) === String(pid);
      });
    });
  }

  /*
     The value cap: the discounted half of a bundle may never outvalue the
     pack that qualifies it.

     The merchant's rule, stated on a real case: buying two starter packs and
     getting 27% off one is fine - buying a $34.99 starter to unlock 27% off
     a $99.99 party pack is not. The cheaper item is always the one the
     discount lands on, so any offer placed in a discounted slot must cost no
     more than the most expensive base in play.

     The cap is seeded from what is already known before any picking starts:
     the product being viewed when it is itself a base, and the priciest base
     sitting in the cart. fromBundle raises it further the moment it picks a
     base row. Zero means "no base in play yet", which never advertises a
     discount, so nothing needs capping.
  */
  function computeGetCap(o) {
    var tree = o.tree || {};
    var b = tree.bundle || {};
    if (!b.base) return 0;
    var cap = 0;
    if (o.excludeProductId && inCats(tree, [b.base], o.excludeProductId)) {
      cap = o.anchorPrice || 0;
    }
    (((o.cart || {}).items) || []).forEach(function (it) {
      if (inCats(tree, [b.base], it.product_id) && (it.price || 0) > cap) {
        cap = it.price || 0;
      }
    });
    return cap;
  }

  /*
     A cart that is ENTIRELY one brand should only be offered that brand.

     The fence used to run one way - lamps kept out of TOSSIT carts - on the
     reasoning that LUMO surfaces guard themselves. That held while the cart
     page answered an all-lamp cart with silence. The moment it started
     asking the engine instead, the second slot fell through to the generic
     "More ways to play" rule and offered a dart poster to someone buying
     night lights: the same wrong-brand pitch, just pointing the other way.
     Caught in simulation before it shipped.

     Zero-priced lines are brand-neutral (the mystery poster is the store's,
     not a brand signal) and are excluded before the test.
  */
  function cartBrand(tree, cart) {
    var items = ((cart || {}).items || []).filter(function (i) {
      return (i.final_line_price === undefined ? 1 : i.final_line_price) !== 0;
    });
    if (!items.length) return null;
    var lumoIds = ((tree || {}).categories || {}).lumo || [];
    var isLumo = function (pid) {
      return lumoIds.some(function (x) { return String(x) === String(pid); });
    };
    var all = items.every(function (i) { return isLumo(i.product_id); });
    return all ? 'lumo' : null;
  }

  function cartHasCategory(tree, idx, key) {
    return (((tree || {}).categories || {})[key] || []).some(function (id) {
      return idx.products[Number(id)];
    });
  }

  /*
     The displacement guard's threshold.

     Shopify discounts only the maxGets CHEAPEST qualifying add-ons, so once
     a cart holds a full bundle, offering a CHEAPER add-on is offering to
     shrink the customer's discount: the cheap item enters the discounted
     pair and kicks the dearest one out. Measured live - a completed
     starter+expansion+poster bundle pitched a second poster, and taking it
     cost the customer $2.19 of discount while charging $8.88 more.

     getsKick is the price of the dearest DISCOUNTED add-on once the bundle
     is full, or 0 while it is not. admit() refuses gets-category items
     priced below it; an item at or above the kick price leaves the
     discounted pair alone and stays offerable.
  */
  function computeGetsKick(o) {
    var tree = o.tree || {};
    var b = tree.bundle || {};
    if (!b.base || !b.gets) return 0;
    var maxGets = b.maxGets || b.minGets || 2;
    var prices = [];
    var hasBase = false;
    (((o.cart || {}).items) || []).forEach(function (it) {
      if (inCats(tree, [b.base], it.product_id)) hasBase = true;
      if (o.excludeProductId && String(it.product_id) === String(o.excludeProductId)) return;
      if (inCats(tree, b.gets, it.product_id)) {
        for (var q = 0; q < (it.quantity || 1); q++) prices.push(it.price || 0);
      }
    });
    if (o.excludeProductId && inCats(tree, b.gets, o.excludeProductId)) {
      prices.push(o.anchorPrice || 0);
      if (inCats(tree, [b.base], o.excludeProductId)) hasBase = true;
    }
    if (!hasBase || prices.length < maxGets) return 0;
    prices.sort(function (x, y) { return x - y; });
    return prices[maxGets - 1] || 0;
  }

  function makeBand(o) {
    return {
      ceiling: o.maxTotal || 0,
      running: o.anchorPrice || 0,
      getCap: computeGetCap(o),
      getsKick: computeGetsKick(o),
      // Carried on the band because it is the one context object every
      // source already hands to admit() - the brand fence reads these.
      intent: o.intent || null,
      seed: o.seed || '',
      cartBrand: cartBrand(o.tree, o.cart),
      // A TOSSIT product page is an explicit signal that overrides the cart.
      anchorIsTossit: !!(o.excludeProductId && productCategory(o.tree, o.excludeProductId) &&
                         productCategory(o.tree, o.excludeProductId) !== 'lumo'),
      anchorIsLumo: !!(o.excludeProductId && productCategory(o.tree, o.excludeProductId) === 'lumo')
    };
  }

  function fits(band, price) {
    if (!band || !band.ceiling) return true;
    return band.running + (price || 0) <= band.ceiling;
  }

  function spend(band, price) {
    if (band) band.running += price || 0;
  }

  /*
     Owning a product no longer disqualifies it - owning EVERY COLOUR of it
     does.

     The old rule skipped any product already in the cart, which read as
     sensible until a real customer hit it: someone holding the Red + Cyan
     starter pack asked why they could not add a Blue + Yellow one to the
     bundle. A second starter pack in a new colourway is not a duplicate, it
     is two more players - the most natural expansion this catalogue has.

     So: a product in the cart may still be offered when it has at least two
     available variants and at least one of them is NOT in the cart. The
     offer's add-to-cart id becomes that unowned variant, so pressing Add can
     never produce an accidental duplicate of the exact colour they already
     chose. Products with a single variant keep the old rule - a literal
     duplicate helps nobody.

     Returns the variant object to offer, or null when the product should be
     skipped after all.
  */
  /*
     Which colourway to offer for a product NOT in the cart.

     It used to be p.variantId every time - Shopify's
     selected_or_first_available_variant - so every surface pushed Red +
     Cyan forever and the other six colourways were effectively unsellable
     through the upsells. Reported exactly that way.

     Rotated deterministically instead: the offset comes from the seed the
     caller passes (the product being viewed) plus the product's own id, so
     a given page always shows the same colour - stable across reloads, no
     shifting under the customer - while different pages surface different
     ones. Availability is respected, and a single-variant product is
     unaffected.
  */
  function offerVariant(p, seed) {
    /*
       ONLY variants that cost what the card says.

       The catalogue emits ONE price per product (the first available
       variant's), and every surface prices off it. Variant prices are NOT
       uniform here - the Lavender expansion is $23.99 against a $16.99
       card, the Purple + Green starter $29.99 against $34.99 - so rotating
       freely made the button quote one number and add another. That is the
       theme deciding money, which it may never do.

       Rotation is still real: the starter has three colourways at $34.99
       and the expansion five at $16.99. An oddly-priced variant simply
       never arrives by rotation, which is correct - a different price is a
       different offer and deserves its own pitch, not a silent swap.
    */
    var base = p.price;
    var avail = (p.variants || []).filter(function (v) {
      return v.available && (v.price === undefined || v.price === base);
    });
    if (avail.length < 2) return null;
    var key = String(seed || '') + String(p.variantId || '');
    var n = 0;
    for (var i = 0; i < key.length; i++) n = (n * 31 + key.charCodeAt(i)) % 100003;
    // The whole VARIANT, not just its id - the id alone rotated the colour
    // being ADDED while every card kept showing the first variant's photo.
    // A picture of Red + Cyan on a button that adds Pink + Blue is worse
    // than no rotation at all. Same hash, so nobody's colour changes.
    return avail[n % avail.length];
  }

  function repeatVariant(p, idx) {
    var avail = (p.variants || []).filter(function (v) { return v.available; });
    if (avail.length < 2) return null;
    for (var i = 0; i < avail.length; i++) {
      if (!idx.variants[Number(avail[i].id)]) return avail[i];
    }
    return null;
  }

  /*
     One gate for every source: should this catalogue entry be skipped, and
     if not, which variant should the Add button carry?

     Returns null to skip, or { id, repeat } - id being the variant to add.
  */
  function productCategory(tree, id) {
    var cats = (tree || {}).categories || {};
    for (var k in cats) {
      if ((cats[k] || []).some(function (x) { return String(x) === String(id); })) return k;
    }
    return null;
  }

  function admit(id, p, idx, taken, skip, tree, band, catTaken) {
    if (!p) return null;
    if (taken[id] || skip[id]) return null;
    // A zero-priced product is a REWARD, never an offer. The mystery poster
    // is fenced out of every collection, but a fence gets moved eventually -
    // this line is what keeps "$0.00 - Add" out of a sales surface when it
    // does.
    if (!p.price) return null;
    /*
       The value cap, enforced at the one gate every source passes through.
       A constraint checked in only one picker is a constraint that does not
       exist - the band was inert for months for exactly that reason - so
       this lives here, where fromBundle, fromShopify, fromTree and the
       catalog fill cannot route around it. Hard, deliberately: when the only
       way to finish a bundle is an item worth more than its base, the bundle
       goes unfinished. The merchant's rule outranks completion.
    */
    if (band && band.getCap > 0 && tree && tree.bundle &&
        inCats(tree, tree.bundle.gets, id) && (p.price || 0) > band.getCap) {
      return null;
    }
    // The displacement guard - see computeGetsKick. A full bundle is never
    // pitched an add-on that would shrink its own discount.
    if (band && band.getsKick > 0 && tree && tree.bundle &&
        inCats(tree, tree.bundle.gets, id) && (p.price || 0) < band.getsKick) {
      return null;
    }
    /*
       The brand fence. LUMO is a different brand with a different buyer,
       and a night light appearing beside a dart set unasked reads as a
       glitch, not a suggestion. A lamp may cross onto a TOSSIT surface
       only when something real says this shopper is a lamp person: the
       cart already holds one, the page being viewed is one, or the
       device's own browsing leans lumo (intent affinity over a third).
       The same gate in reverse is deliberately absent - TOSSIT products
       never cross into LUMO surfaces through the engine at all; the lamp
       pages have their own machinery.
    */
    var cat = productCategory(tree, id);
    if (cat === 'lumo') {
      var lumoOk =
        cartHasCategory(tree, idx, 'lumo') ||
        (band && band.anchorIsLumo) ||
        (band && band.intent && (band.intent.affinity || {}).lumo >= 0.34);
      if (!lumoOk) return null;
    } else if (band && band.cartBrand === 'lumo' && !band.anchorIsTossit) {
      // and the same fence pointing the other way
      return null;
    }
    var owned = idx.products[Number(id)] || idx.variants[Number(p.variantId)];
    if (!owned) {
      /*
         One row per category, and the anchor's own category counts.

         Two posters in one bundle box is not a bundle, it is the same idea
         twice - seen live once the expansion slot ran dry and the catalog
         fill grabbed a second `surface` item the band was too cheap to
         veto. The categories are the box's notion of "kinds of thing", so
         the rule is simply: one of each kind per surface.

         Checked only for products NOT already in the cart, on purpose. A
         repeat row - another colourway of something the customer owns - is
         the "add another player" offer the merchant explicitly wants, and
         it should never lose its slot to this rule.
      */
      if (catTaken) {
        var cat = productCategory(tree, id);
        // lumo is exempt from one-per-category: the lamps are a cast of
        // characters, and "another friend" is the entire pitch. Two lamp
        // cards is a sleepover; two posters is the same idea twice.
        if (cat && cat !== 'lumo' && catTaken[cat]) return null;
      }
      var cv = offerVariant(p, band && band.seed);
      return {
        id: cv ? cv.id : p.variantId,
        repeat: false,
        image: (cv && cv.image) || null,
        title: (cv && cv.title) || null
      };
    }
    var rv = repeatVariant(p, idx);
    return rv ? { id: rv.id, repeat: true, image: rv.image || null, title: rv.title || null } : null;
  }

  function fromTree(tree, idx, opts) {
    var catTaken = opts.catTaken || {};
    if (!tree || !tree.rules || !tree.rules.length) return [];

    var band = opts.band;
    var limit = opts.limit;
    var taken = opts.taken;
    var skip = opts.skip || {};
    var present = categoriesInCart(tree, idx.products);
    var out = [];

    for (var r = 0; r < tree.rules.length && out.length < limit; r++) {
      var rule = tree.rules[r];

      var hasAll = (rule.has || []).every(function (k) { return present[k]; });
      if (!hasAll) continue;
      var lacksAll = (rule.lacks || []).every(function (k) { return !present[k]; });
      if (!lacksAll) continue;

      var ids = rotate((tree.categories || {})[rule.offer] || [], opts.seed);
      for (var i = 0; i < ids.length; i++) {
        var id = ids[i];
        var p = (tree.catalog || {})[id];
        if (!p) continue;
        var adm = admit(String(id), p, idx, taken, skip, tree, band, catTaken);
        if (!adm) continue;
        if (!fits(opts.band, p.price)) continue;

        spend(opts.band, p.price);
        taken[id] = true;
        if (!adm.repeat) { var ct1 = productCategory(tree, id); if (ct1) catTaken[ct1] = true; }
        out.push({
          repeat: adm.repeat,
          preferVariantId: adm.id, // the box preselects the rotated colour too
          id: adm.id,
          productId: String(id),
          title: p.title,
          price: p.price,
          compareAt: p.compareAt,
          image: adm.image || p.image,
          url: p.url,
          variants: p.variants || [],
          heading: rule.heading,
          source: 'rules'
        });
        break;
      }
    }
    return out;
  }

  /* ------------------------------------------------------------------ *
     The page's own bundle

     What belongs beside a product depends on what product it is, so this runs
     BEFORE the cart rules and before Shopify's suggestions. The order comes
     from snippets/tossit-upsell-rules.liquid, where every non-darts page
     leads with darts - a poster or a board with nothing to throw at it is an
     unusable order, and that rule should not depend on what happens to be in
     the cart at the time.

     One product per category, so a three-item bundle is three different
     KINDS of thing rather than three posters.
   * ------------------------------------------------------------------ */

  function fromBundle(tree, idx, opts) {
    var catTaken = opts.catTaken || {};
    var order = (tree && tree.bundleOrder) || [];
    if (!order.length) return [];

    var limit = opts.limit;
    var taken = opts.taken;
    var skip = opts.skip || {};
    var out = [];

    /*
       Keep the bundle inside a price band.

       Rotation alone picked whatever came next in the category, which on a
       $59.99 family pack meant offering a $59.99 board and then a $49.99
       board - a $169.97 "bundle" of two near-identical boards. Nobody adds
       three of the most expensive things in the catalogue on impulse, and
       two boards is not a bundle, it is a mistake.

       So each category's candidates are tried CHEAPEST FIRST, and anything
       that would push the running total past the ceiling is skipped.

       The band itself is now SHARED with every other source (see makeBand
       above). It used to be private to this function, read from opts that
       gather() never actually forwarded - so the ceiling was 0 here, the
       cheapest-first sort never ran, and the whole band was inert.
    */
    var band = opts.band;
    var ceiling = band ? band.ceiling : 0;

    /*
       QUALIFICATION OUTRANKS THE BAND.

       The band exists to stop absurd bundles (two $60 boards on a $35
       starter). But applied blindly it produced something worse: a bundle
       the customer could not qualify for at all. Seen live on the Expansion
       Pack page - the box picked a $99.99 Party Pack as the base, the band
       then vetoed every remaining add-on, and the customer was told to
       "tick 1 more" with nothing left to tick. A box that demands a third
       item while refusing to offer one is not protecting the order value,
       it is refusing the discount on the customer's behalf.

       Two rules restore sanity:
       - picking a BASE reserves budget for the cheapest gets still needed,
         so an expensive base cannot spend the room the discount requires;
       - picking a GET ignores the ceiling entirely while the bundle is
         still short of minGets. Cheapest-first makes the overage minimal.
    */
    var b = tree.bundle || {};
    var getsCats = b.gets || [];
    var minGets = b.minGets || 0;
    var inCat = function (c, pid) {
      return ((tree.categories || {})[c] || []).some(function (x) { return String(x) === String(pid); });
    };
    var anchorIsGet = opts.anchorProductId
      ? getsCats.some(function (c) { return inCat(c, opts.anchorProductId); })
      : false;
    var getsPicked = anchorIsGet ? 1 : 0;

    // The cheapest way to finish the bundle from here: sum of the N cheapest
    // still-offerable gets. What a base pick must leave room for.
    function cheapestGetsSum(needed) {
      if (needed <= 0) return 0;
      var prices = [];
      getsCats.forEach(function (c) {
        (((tree.categories || {})[c]) || []).forEach(function (id) {
          var p = (tree.catalog || {})[id];
          if (admit(String(id), p, idx, taken, skip, tree, band)) prices.push(p.price || 0);
        });
      });
      prices.sort(function (a, x) { return a - x; });
      var sum = 0;
      for (var i = 0; i < needed && i < prices.length; i++) sum += prices[i];
      return sum;
    }

    for (var c = 0; c < order.length && out.length < limit; c++) {
      var catKey = order[c];
      var isGet = getsCats.indexOf(catKey) > -1;
      var ids = rotate((tree.categories || {})[catKey] || [], opts.seed);

      if (ceiling > 0) {
        ids = ids.slice().sort(function (a, x) {
          var pa = (tree.catalog || {})[a], pb = (tree.catalog || {})[x];
          return ((pa && pa.price) || 0) - ((pb && pb.price) || 0);
        });
      }

      var pick = null;     // first candidate that satisfies the band rules
      var fallback = null; // cheapest admissible candidate, band be damned
      for (var i = 0; i < ids.length; i++) {
        var id = String(ids[i]);
        var p = (tree.catalog || {})[id];
        var adm = admit(id, p, idx, taken, skip, tree, band, catTaken);
        if (!adm) continue;
        if (!fallback) fallback = { id: id, p: p, adm: adm };

        if (ceiling > 0) {
          if (isGet) {
            // Over-ceiling is allowed only while the bundle cannot yet qualify.
            if (!fits(band, p.price) && getsPicked >= minGets) continue;
          } else {
            // A base must leave room to finish the bundle it starts.
            var reserve = cheapestGetsSum(minGets - getsPicked);
            if (band.running + p.price + reserve > ceiling) continue;
          }
        }

        pick = { id: id, p: p, adm: adm };
        break;
      }

      /*
         Nothing fit the band: fall back to the cheapest admissible candidate
         ONLY where the slot is load-bearing - a base (the bundle has no
         pitch without one) or a get the bundle still needs to qualify. A
         gets slot that is already satisfied simply goes unfilled, exactly as
         it always did; that is the band doing its actual job.
      */
      if (!pick && fallback && (!isGet || getsPicked < minGets)) pick = fallback;

      if (pick) {
        spend(band, pick.p.price);
        taken[pick.id] = true;
        if (!pick.adm.repeat) catTaken[catKey] = true;
        if (isGet) getsPicked++;
        // A base row just entered the bundle: every discounted slot after
        // this point is capped by it. Never lowered - the priciest base in
        // play (cart, anchor or pick) is the one the rule measures against.
        if (!isGet && band && pick.p.price > (band.getCap || 0)) {
          band.getCap = pick.p.price;
        }
        out.push({
          id: pick.adm.id, productId: pick.id, title: pick.p.title,
          price: pick.p.price, compareAt: pick.p.compareAt,
          image: pick.adm.image || pick.p.image, url: pick.p.url,
          variants: pick.p.variants || [],
          repeat: pick.adm.repeat, preferVariantId: pick.adm.id,
          heading: opts.bundleHeading || 'Completes the set',
          source: 'bundle'
        });
      }
    }
    return out;
  }

  /* ------------------------------------------------------------------ *
     Shopify's own suggestions

     Complementary products are set per product in the free Search & Discovery
     app - the merchant's own lever, no code and no deploy. They are tried
     FIRST because a human chose them deliberately; the category tree is the
     fallback for the products nobody has curated yet.
   * ------------------------------------------------------------------ */

  function fromShopify(tree, idx, opts) {
    var catTaken = opts.catTaken || {};
    var list = opts.recommended || [];
    var band = opts.band;
    var limit = opts.limit;
    var taken = opts.taken;
    var skip = opts.skip || {};
    var out = [];

    for (var i = 0; i < list.length && out.length < limit; i++) {
      var id = String(list[i]);
      var p = (tree && tree.catalog ? tree.catalog : {})[id];
      // Only offer products the catalog knows about - it carries the price and
      // image, and a recommendation we cannot render is worse than none.
      if (!p) continue;
      var adm = admit(id, p, idx, taken, skip, tree, band, catTaken);
      if (!adm) continue;
      // A merchant-curated recommendation still has to fit the band. It is a
      // suggestion about relevance, not permission to blow the ceiling.
      if (!fits(opts.band, p.price)) continue;

      spend(opts.band, p.price);
      taken[id] = true;
      if (!adm.repeat) { var ct2 = productCategory(tree, id); if (ct2) catTaken[ct2] = true; }
      out.push({
        repeat: adm.repeat,
        preferVariantId: adm.id, // the box preselects the rotated colour too
        id: adm.id,
        productId: id,
        title: p.title,
        price: p.price,
        compareAt: p.compareAt,
        image: adm.image || p.image,
        url: p.url,
        variants: p.variants || [],
        heading: opts.recommendedHeading || 'Goes well with this',
        source: 'shopify'
      });
    }
    return out;
  }

  /* ------------------------------------------------------------------ *
     The public call
   * ------------------------------------------------------------------ */

  function pick(options) {
    var o = options || {};
    var picked = gather(o, o.respectDismissed !== false);

    /*
       A dismissal must never be able to empty the surface.

       Dismissing is a soft "not that one", not an opt-out from being offered
       anything ever. With a small catalogue it took ONE press to leave nothing
       eligible, at which point the box hid itself and stayed hidden for the
       rest of the session - indistinguishable from broken, and reported as
       exactly that.

       So if honouring the dismissals leaves nothing, run again ignoring them.
       The customer still never sees the item they just closed while there is
       anything else to show; they only see it again when it is that or an
       empty box, and an empty box helps nobody.
    */
    /*
       ...and a dismissal must never SHRINK the surface either. The rule
       above only refired when the box was completely empty, so pressing X
       on one of two suggestions just deleted the row - the replacement the
       customer reasonably expected never came, and a bundle that needed
       that slot could no longer be completed. Reported live, twice.

       Now any shortfall backfills, still ignoring dismissals only for the
       missing slots and never re-offering what is already on screen. The
       item just closed only returns when the alternative is a gap.
    */
    var want = o.limit || 2;
    if (picked.length < want && o.respectDismissed !== false && dismissed().length) {
      var have = {};
      picked.forEach(function (p) { have[String(p.productId)] = true; });
      var again = {};
      for (var k in o) again[k] = o[k];
      again.limit = want - picked.length;
      again.preTaken = have;
      var haveCats = {};
      picked.forEach(function (p) {
        if (p.repeat) return;
        var hc = productCategory(o.tree, p.productId);
        if (hc) haveCats[hc] = true;
      });
      again.preTakenCats = haveCats;
      picked = picked.concat(gather(again, false));
    }

    return picked;
  }

  /*
     Rotate a category's candidates so the same product is not always the one
     shown.

     "Boards & posters" holds four products, and a category that always
     returned its first member meant the Tic-Tac-Toe poster and the Family
     Board were effectively unsellable through the upsells - they existed in
     the catalogue and were never once offered.

     Deterministic, not random: the offset is derived from a seed the caller
     passes (the product being viewed). So a given product page always shows
     the same suggestions - stable across reloads, safe to cache, no layout
     shifting under the customer - while different product pages surface
     different members of the same category.
  */
  function rotate(ids, seed) {
    if (!ids || ids.length < 2) return ids || [];
    var n = 0;
    var s = String(seed == null ? '' : seed);
    for (var i = 0; i < s.length; i++) n = (n * 31 + s.charCodeAt(i)) >>> 0;
    var at = n % ids.length;
    return ids.slice(at).concat(ids.slice(0, at));
  }

  function gather(o, respectDismissed) {
    var tree = o.tree;
    var cart = o.cart || { items: [] };
    var limit = o.limit || 2;

    var idx = indexCart(cart);
    var taken = {};
    /*
       One row per category for the whole gather, shared across every source
       so the catalog fill cannot re-fill a category an earlier source used.
       Seeded with the anchor's own category: a poster page offering a second
       poster is the exact duplication this prevents.
    */
    var catTaken = {};
    if (o.excludeProductId) {
      var anchorCat = productCategory(tree, o.excludeProductId);
      if (anchorCat) catTaken[anchorCat] = true;
    }
    if (o.preTakenCats) {
      for (var pc in o.preTakenCats) catTaken[pc] = true;
    }
    // Seeded by the shortfall backfill in pick(), so a second gather can
    // never re-offer a product the first one already placed on screen.
    if (o.preTaken) {
      for (var pt in o.preTaken) taken[pt] = true;
    }

    // Never offer the product being looked at, and never re-offer something
    // the shopper has already closed on another surface.
    var skip = {};
    if (o.excludeProductId) skip[String(o.excludeProductId)] = true;
    if (respectDismissed) {
      dismissed().forEach(function (id) { skip[id] = true; });
    }

    // The page's own bundle leads. Only a surface that knows which product is
    // being viewed passes bundleFirst - the cart drawer has no anchor, so it
    // falls straight through to Shopify's suggestions and the cart rules.
    // One band for the whole gather, so the ceiling holds whichever source
    // ends up filling a slot. Surfaces that pass no maxTotal (the cart drawer,
    // the exit popup) get ceiling 0 and are unaffected.
    var band = makeBand(o);

    var picked = o.bundleFirst === false ? [] : fromBundle(tree, idx, {
      limit: limit, taken: taken, skip: skip, seed: o.seed, catTaken: catTaken,
      bundleHeading: o.bundleHeading, band: band,
      // The anchor may itself be one of the bundle's "gets" (an expansion or
      // poster page); fromBundle counts it toward minGets so the reservation
      // and override maths match what the box will claim.
      anchorProductId: o.excludeProductId
    });

    if (picked.length < limit) {
      picked = picked.concat(fromShopify(tree, idx, {
        catTaken: catTaken,
        limit: limit - picked.length, taken: taken, skip: skip,
        recommended: o.recommended, recommendedHeading: o.recommendedHeading,
        band: band
      }));
    }

    if (picked.length < limit) {
      picked = picked.concat(fromTree(tree, idx, {
        catTaken: catTaken,
        limit: limit - picked.length, taken: taken, skip: skip, seed: o.seed,
        band: band
      }));
    }

    // Last resort: anything in the catalog we have not already used. Keeps a
    // box from rendering empty on a product nobody has written rules for.
    if (picked.length < limit && o.fillFromCatalog !== false && tree && tree.catalog) {
      /*
         Intent is allowed exactly one lever, and this is it: the LAST
         RESORT fill re-orders toward what this device actually browses.
         Rules keep their merchant-authored order, the bundle keeps its
         logic, the band and value cap veto whatever they always vetoed -
         intent only decides which valid candidate goes first when nothing
         more specific had an opinion. Rotation still runs underneath as
         the tie-breaker, so an empty profile changes nothing at all.
      */
      var fillIds = rotate(Object.keys(tree.catalog), o.seed);
      var aff = (o.intent && o.intent.affinity) || null;
      if (aff && Object.keys(aff).length) {
        fillIds = fillIds.slice().sort(function (a, x) {
          var ca = productCategory(tree, a), cx = productCategory(tree, x);
          return ((aff[cx] || 0) - (aff[ca] || 0));
        });
      }
      fillIds.forEach(function (id) {
        if (picked.length >= limit) return;
        var p = tree.catalog[id];
        var adm = admit(String(id), p, idx, taken, skip, tree, band, catTaken);
        if (!adm) return;
        // The band binds here too. This is the source that actually produced
        // the two-board bundle: once the cheap add-ons were in the cart there
        // was nothing left for the earlier sources, and the last resort filled
        // both slots from the top of the price list. A shorter bundle is
        // better than an absurd one - the anchor product is still on the card.
        if (!fits(band, p.price)) return;
        spend(band, p.price);
        taken[id] = true;
        if (!adm.repeat) { var ct3 = productCategory(tree, id); if (ct3) catTaken[ct3] = true; }
        picked.push({
          repeat: adm.repeat, preferVariantId: adm.id, // the box preselects the rotated colour too
          id: adm.id, productId: id, title: p.title, price: p.price,
          compareAt: p.compareAt, image: adm.image || p.image, url: p.url,
          variants: p.variants || [], heading: o.fallbackHeading || 'More ways to play',
          source: 'catalog'
        });
      });
    }

    return picked;
  }

  /*
     Attribution, order-level instead of line-level.

     The old way rode a `_tossit_upsell` property on each line, which had a
     cost nobody priced in: Shopify only merges identical lines when their
     properties match, so an upsell-added starter and a hand-added starter
     sat as separate rows of the same variant - three "Starter Pack Red +
     Cyan" lines in one cart, which reads as a glitch and splits how
     discounts display across them.

     A cart ATTRIBUTE has no such side effect: one key on the order, a
     comma-set of which surfaces sold something. Same underscore-prefix
     convention, same place in the order payload reporting reads from.
     Fire-and-forget on purpose - attribution must never be able to delay
     or break an add-to-cart.
  */
  function attribute(tag) {
    try {
      /*
         The intent bucket rides the tag - "cart-offer@lumo-high" in an
         order's attribute means a device that mostly browsed lamps
         converted on a drawer card. This is the entire feedback loop:
         the weekly read of orders in admin now says which intent reads
         actually buy, with no pixel and no app.
      */
      if (window.TossitIntent && window.TossitIntent.bucket) {
        tag = tag + '@' + window.TossitIntent.bucket();
      }
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (c) {
          var prev = (c.attributes && c.attributes._tossit_upsell) || '';
          if ((',' + prev + ',').indexOf(',' + tag + ',') > -1) return;
          var next = prev ? prev + ',' + tag : tag;
          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ attributes: { _tossit_upsell: next } })
          });
        })
        .catch(function () {});
    } catch (e) {}
  }

  /*
     The gate, exposed - because a second picker WILL route around it.

     The tier-gap card on both cart surfaces walks the catalog itself
     (cheapest thing that crosses the line) and so skipped every guard this
     file owns. Harmless until the LUMO lamps entered the catalog, at which
     point a pure-TOSSIT cart could be shown a night light as its
     free-shipping filler - the exact cross-brand pitch the fence exists to
     stop, arriving through the one card both surfaces put FIRST.

     Anything that offers a product must ask this. It answers the three
     questions that are not about price: is it the wrong brand for this
     cart, has the shopper already closed it, and is it a zero-priced
     reward that must never be sold.
  */
  function offerable(tree, id, p, cart, opts) {
    var o = opts || {};
    if (!p) return false;
    if ((p.price || 0) <= 0) return false;                 // the reward is not merchandise
    if (dismissed().indexOf(String(id)) > -1) return false; // they closed it
    var cat = productCategory(tree, id);
    if (cat === 'lumo') {
      var idx = indexCart(cart || { items: [] });
      var lean = (o.intent && o.intent.affinity && o.intent.affinity.lumo) || 0;
      if (!cartHasCategory(tree, idx, 'lumo') && !o.anchorIsLumo && lean < 0.34) return false;
    } else if (cartBrand(tree, cart) === 'lumo') {
      return false;   // an all-lamp cart is not shown darts
    }
    return true;
  }

  return {
    pick: pick,
    dismiss: dismiss,
    dismissed: dismissed,
    indexCart: indexCart,
    categoriesInCart: categoriesInCart,
    productCategory: productCategory,
    offerable: offerable,
    offerVariant: offerVariant,
    attribute: attribute
  };
})();
